import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Camera, Eye, FilePdf, MapPin, Plus, UsersThree } from "@phosphor-icons/react";
import { aender, lies, schick } from "../../api/client";
import type { Projekt, Tag, TagKurz } from "../../api/types";
import { Meldung } from "../../components/Bausteine";
import { Ortssuche } from "../../components/Ortssuche";
import { datum, heute } from "../../lib/format";

export const WOCHENTAG = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Montag und Sonntag der Woche von `iso` — Vorgabe fuer den Druckzeitraum. */
function woche(iso: string): [string, string] {
  const t = new Date(`${iso}T12:00:00`);
  const mo = new Date(t);
  mo.setDate(t.getDate() - ((t.getDay() + 6) % 7));
  const so = new Date(mo);
  so.setDate(mo.getDate() + 6);
  const f = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return [f(mo), f(so)];
}

/** Bautagebuch (SPEC §14): je Baustellentag ein Eintrag, das PDF je Zeitraum. */
export function TagebuchReiter({ projekt, kann }: { projekt: Projekt; kann: boolean }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const liste = useQuery({
    queryKey: ["tagebuch", projekt.id],
    queryFn: () => lies<TagKurz[]>(`/projekte/${projekt.id}/tagebuch`),
  });
  const [tag, setTag] = useState(heute());
  const [[von, bis], setZeitraum] = useState(woche(heute()));
  const anlegen = useMutation({
    mutationFn: () => schick<Tag>(`/projekte/${projekt.id}/tagebuch`, { datum: tag }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["tagebuch", projekt.id] });
      nav(`/t/${t.id}`, { state: { hinweis: t.hinweis } });
    },
  });
  const vorhanden = liste.data?.find((t) => t.datum === tag);
  const druck = `/projekte/${projekt.id}/tagebuch`;

  return (
    <>
      {!projekt.lat && <Standort projekt={projekt} />}
      <section className="karte">
        <div className="karte-kopf">
          <h2>Bautagebuch</h2>
          <div className="luft" />
          {kann && (
            <div className="zeile-knoepfe">
              <input type="date" className="eingabe" value={tag} aria-label="Tag" onChange={(e) => setTag(e.target.value)} />
              {vorhanden
                ? <Link className="knopf" to={`/t/${vorhanden.id}`}>Öffnen</Link>
                : <button className="knopf haupt" disabled={anlegen.isPending || !tag} onClick={() => anlegen.mutate()}>
                    <Plus size={16} weight="bold" /> Tag anlegen</button>}
            </div>
          )}
        </div>
        <Meldung fehler={liste.error || anlegen.error} />
        <div className="liste">
          {liste.data?.map((t) => (
            <Link key={t.id} to={`/t/${t.id}`} className="zeile">
              <span className="nr ziffern">{WOCHENTAG[new Date(`${t.datum}T12:00:00`).getDay()]} {datum(t.datum)}</span>
              <div className="haupt">
                <div className="einzeilig">{t.leistungen || <span className="mute">noch keine Leistungen eingetragen</span>}</div>
                <div className="mute">{t.wetter_kurz || "kein Wetter"}</div>
              </div>
              <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {t.personal > 0 && <span className="chip"><UsersThree size={13} /> {t.personal}</span>}
                {t.fotos > 0 && <span className="chip"><Camera size={13} /> {t.fotos}</span>}
                {t.status === "entwurf" && <span className="chip warn">offen</span>}
              </span>
            </Link>
          ))}
          {liste.data?.length === 0 && <div className="leer">Noch kein Tag eingetragen.</div>}
        </div>
      </section>

      <section className="karte">
        <div className="karte-kopf"><h2>Ausdruck</h2><span className="mute">für einen Zeitraum, höchstens drei Monate</span></div>
        <div className="zeile-knoepfe">
          <input type="date" className="eingabe" value={von} aria-label="von" onChange={(e) => setZeitraum([e.target.value, bis])} />
          <span className="mute">bis</span>
          <input type="date" className="eingabe" value={bis} aria-label="bis" onChange={(e) => setZeitraum([von, e.target.value])} />
          <a className="knopf" href={`/api${druck}/druck?von=${von}&bis=${bis}`} target="_blank" rel="noreferrer"><Eye size={16} /> Druckansicht</a>
          <a className="knopf" href={`/api${druck}/pdf?von=${von}&bis=${bis}`} target="_blank" rel="noreferrer"><FilePdf size={16} /> PDF</a>
        </div>
      </section>
    </>
  );
}

/** Ohne Koordinaten kein Wetter. Auf der Baustelle reicht ein Tipp: der Standort des
 *  Tablets wird zum Ort des Projekts (nur die Leitung darf das Projekt aendern). */
export function Standort({ projekt }: { projekt: Projekt }) {
  const qc = useQueryClient();
  const [fehler, setFehler] = useState("");
  const setzen = useMutation({
    mutationFn: (r: { lat: number; lon: number }) => aender<Projekt>(`/projekte/${projekt.id}`, r),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projekt"] });
      qc.invalidateQueries({ queryKey: ["tag"] });
    },
  });
  const holen = () => {
    setFehler("");
    if (!navigator.geolocation) return setFehler("Dieses Gerät gibt keinen Standort heraus.");
    navigator.geolocation.getCurrentPosition(
      (p) => setzen.mutate({ lat: Math.round(p.coords.latitude * 1e5) / 1e5, lon: Math.round(p.coords.longitude * 1e5) / 1e5 }),
      () => setFehler("Standort nicht freigegeben. In den Einstellungen des Browsers erlauben oder in den Projekt-Einstellungen eintragen."),
      { enableHighAccuracy: false, timeout: 15000 });
  };
  return (
    <div className="hinweis-karte">
      <MapPin size={18} />
      <div className="haupt">
        <div>Für das Wetter vom Deutschen Wetterdienst braucht das Projekt den Ort der Baustelle.</div>
        {projekt.meine_rolle !== "leitung" && <div className="mute">Das kann die Projektleitung einstellen.</div>}
        {projekt.meine_rolle === "leitung" && (
          <div style={{ marginTop: 8 }}>
            <Ortssuche gewaehlt={(o) => setzen.mutate({ lat: o.lat, lon: o.lon })} />
          </div>
        )}
        {fehler && <div className="mute">{fehler}</div>}
        <Meldung fehler={setzen.error} />
      </div>
      {projekt.meine_rolle === "leitung" && (
        <button className="knopf" disabled={setzen.isPending} onClick={holen}><MapPin size={16} /> Hier bin ich</button>
      )}
    </div>
  );
}
